import { useCallback, useEffect, useRef, useState } from 'react';
import type { MatchStateEntry } from './useMatchEventsSocket';

export interface GoalAlert {
  /** Unique per raised alert (a fixture can score more than once). */
  key: string;
  fixtureId: number;
  home: string;
  away: string;
  /** Post-goal scoreline, en-dash (e.g. "2–1"). */
  score: string;
  side: 'home' | 'away';
}

interface ActiveMatchLike {
  fixtureId: number;
  homeTeam: string;
  awayTeam: string;
}

type ScorePair = { h: number; a: number };

/**
 * PURE diff: which fixtures' score INCREASED between `prev` and the current
 * `matchState`. A fixture absent from `prev` is a baseline seed (no goal); a
 * decrease (VAR revert) is not a goal. Reads `prev`, never mutates it.
 */
export function diffGoals(
  prev: Map<number, ScorePair>,
  matchState: Map<number, MatchStateEntry>,
  matches: ActiveMatchLike[],
): Array<Omit<GoalAlert, 'key'>> {
  const out: Array<Omit<GoalAlert, 'key'>> = [];
  matchState.forEach((entry, fx) => {
    const h = entry.state.homeScore ?? 0;
    const a = entry.state.awayScore ?? 0;
    const p = prev.get(fx);
    if (p && (h > p.h || a > p.a)) {
      const m = matches.find((x) => x.fixtureId === fx);
      out.push({
        fixtureId: fx,
        home: m?.homeTeam ?? 'Home',
        away: m?.awayTeam ?? 'Away',
        score: `${h}–${a}`,
        side: h > p.h ? 'home' : 'away',
      });
    }
  });
  return out;
}

/**
 * Cross-match GOAL detector for the Live Match Events hub. Watches the backend-
 * authoritative `match_state` for ALL active fixtures and, when a fixture's score
 * increases, raises a goal alert (which match + side + new scoreline) and stamps
 * `lastGoalAt` so the hub can float that match to the top.
 *
 * Uses the backend state, NOT the raw frames, so it fires once per real goal — the
 * confidence ramp, `Cancelled` retractions and per-period reconstruction are all
 * resolved server-side. The FIRST time a fixture's state is seen only seeds the
 * baseline (no alert on tab open / a fixture joining); a fixture leaving the active
 * set is pruned so a re-add re-seeds instead of firing a stale alert.
 */
export function useGoalAlerts(
  matchState: Map<number, MatchStateEntry>,
  matches: ActiveMatchLike[],
): {
  alerts: GoalAlert[];
  dismiss: (key: string) => void;
  lastGoalAt: Map<number, number>;
} {
  const prev = useRef<Map<number, ScorePair>>(new Map());
  const seq = useRef(0);
  const [alerts, setAlerts] = useState<GoalAlert[]>([]);
  const [lastGoalAt, setLastGoalAt] = useState<Map<number, number>>(new Map());

  useEffect(() => {
    const fresh = diffGoals(prev.current, matchState, matches);

    // Advance the baseline for every observed fixture, and prune any that left.
    const next = new Map<number, ScorePair>();
    matchState.forEach((entry, fx) => {
      next.set(fx, {
        h: entry.state.homeScore ?? 0,
        a: entry.state.awayScore ?? 0,
      });
    });
    prev.current = next;

    if (fresh.length === 0) return;
    const now = Date.now();
    setAlerts((cur) => [
      ...cur,
      ...fresh.map((g) => ({ ...g, key: `${g.fixtureId}:${(seq.current += 1)}` })),
    ]);
    setLastGoalAt((cur) => {
      const m = new Map(cur);
      fresh.forEach((g) => m.set(g.fixtureId, now));
      return m;
    });
  }, [matchState, matches]);

  const dismiss = useCallback((key: string) => {
    setAlerts((cur) => cur.filter((a) => a.key !== key));
  }, []);

  return { alerts, dismiss, lastGoalAt };
}
